import React, { useEffect } from "react";
import AntdModal from "../../../../components/common/AntdModal";
import { useBeneficiariesStore } from "../../../../config/stores";
import { Form, Select } from "antd";
import AppInput from "../../../../components/common/AppInput";
import AppButton from "../../../../components/common/AppButton";
import usePrevious from "../../../../components/hooks/usePrevious";
import { isEmpty } from "lodash";
import { useSelector } from "react-redux";

const { Option } = Select;

const BeneficiaryForm = () => {
  const beneficiariesStore = useBeneficiariesStore();
  const { createdBeneficiary, beneficiaryToEdit, creating } =
    beneficiariesStore;
  const { projects } = useSelector((state) => state.projectsState);

  const [form] = Form.useForm();

  const onSubmit = (values) => {
    // Convert disability_status string to boolean
    const formData = {
      ...values,
      disability_status: values.disability_status === "true" || values.disability_status === true,
    };
    
    if (beneficiariesStore.creating) {
      beneficiariesStore.createBeneficiary(formData);
    } else {
      beneficiariesStore.updateBeneficiary(formData, beneficiaryToEdit.id);
    }
  };
  const prevState = usePrevious({ createdBeneficiary });

  const closeModal = () => {
    beneficiariesStore.setCreating(false);
  };

  useEffect(() => {
    if (
      !isEmpty(prevState) &&
      !isEmpty(createdBeneficiary) &&
      prevState.createdBeneficiary !== createdBeneficiary
    ) {
      form.resetFields();
      beneficiariesStore.setCreating(true, true, {});
    }
  }, [prevState, createdBeneficiary, form, beneficiariesStore]);

  useEffect(() => {
    if (!isEmpty(beneficiaryToEdit) && !creating) {
      form.setFieldsValue({
        project_id: beneficiaryToEdit.project_id,
        name: beneficiaryToEdit.name,
        gender: beneficiaryToEdit.gender,
        age_category: beneficiaryToEdit.age_category,
        year_enrolled: beneficiaryToEdit.year_enrolled,
        nationality: beneficiaryToEdit.nationality,
        contact: beneficiaryToEdit.contact,
        location: beneficiaryToEdit.location,
        disability_status: String(beneficiaryToEdit.disability_status || false),
        category: beneficiaryToEdit.category || undefined,
        notes: beneficiaryToEdit.notes,
      });
    }
  }, [beneficiaryToEdit, form, creating]);

  return (
    <div>
      <AntdModal
        open={beneficiariesStore.showCreateBeneficiary}
        title={
          beneficiariesStore.creating
            ? "CREATE BENEFICIARY"
            : "UPDATE BENEFICIARY"
        }
        footer={null}
        size="large"
        closeModal={closeModal}
      >
        <Form
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 16 }}
          layout="horizontal"
          onFinish={onSubmit}
          form={form}
        >
          <Form.Item
            name="project_id"
            label="Project"
            rules={[
              {
                required: true,
                message: "Please select a project",
              },
            ]}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              disabled={!creating}
            >
              {projects?.activeProjects?.map((project) => {
                return (
                  <Option value={project.id} key={project.id}>
                    {project.name}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
          <AppInput
            name="name"
            label="Name"
            rules={[
              {
                required: true,
                message: "This field is required",
              },
            ]}
          />
          <Form.Item
            name="gender"
            label="Gender"
            rules={[
              {
                required: true,
                message: "Please select a gender",
              },
            ]}
          >
            <Select allowClear>
              <Option value="Male">Male</Option>
              <Option value="Female">Female</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="age_category"
            label="Age Category"
            rules={[{ required: true, message: "Please select an age category" }]}
          >
            <Select allowClear>
              <Option value="10-14">10-14</Option>
              <Option value="15-19">15-19</Option>
              <Option value="20-24">20-24</Option>
              <Option value="25-30">25-30</Option>
              <Option value="31-45">31-45</Option>
              <Option value="Over 45">Over 45</Option>
            </Select>
          </Form.Item>
          <AppInput
            name="year_enrolled"
            type="number"
            label="Year Enrolled"
            inputAttributes={{
              min: 2000,
              max: new Date().getFullYear() + 1,
            }}
            rules={[{ required: true, message: "This field is required" }]}
          />
          <Form.Item
            name="nationality"
            label="Nationality"
            rules={[
              {
                required: true,
                message: "Please select a nationality",
              },
            ]}
          >
            <Select allowClear>
              <Option value="UGANDAN">Ugandan</Option>
              <Option value="REFUGEE">Refugee</Option>
            </Select>
          </Form.Item>
          <AppInput
            name="contact"
            label="Contact"
            rules={[
              {
                required: false,
              },
            ]}
          />
          <AppInput
            name="location"
            label="Location"
            rules={[
              {
                required: false,
              },
            ]}
          />
          <Form.Item
            name="disability_status"
            label="Disability Status"
            rules={[
              {
                required: true,
                message: "Please select disability status",
              },
            ]}
          >
            <Select allowClear>
              <Option value="false">No</Option>
              <Option value="true">Yes</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="category"
            label="Category"
          >
            <Select allowClear placeholder="Select Category">
              <Option value="Direct Beneficiary">Direct Beneficiary</Option>
              <Option value="Indirect Beneficiary">Indirect Beneficiary</Option>
              <Option value="Participant">Participant</Option>
              <Option value="Community Member">Community Member</Option>
              <Option value="Intermediary">Intermediary</Option>
            </Select>
          </Form.Item>
          <AppInput
            name="notes"
            type="textarea"
            label="Support provided"
            rules={[
              {
                required: false,
              },
            ]}
          />
          <Form.Item className={"mt-3"} wrapperCol={{ offset: 5 }}>
            <AppButton
              text={
                beneficiariesStore.creating
                  ? "Create Beneficiary"
                  : "Update Beneficiary"
              }
              loading={
                beneficiariesStore.creatingBeneficiary ||
                beneficiariesStore.updatingBeneficiary
              }
              type="submit"
            />
          </Form.Item>
        </Form>
      </AntdModal>
    </div>
  );
};

export default BeneficiaryForm;

